import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, ArrowUpRight } from 'lucide-react';
import { RosterMetric } from './RosterMetric';
import { rosterCopy, rosterDate, rosterNumber } from './rosterCopy';
import './rosterCards.css';

export const SmartAthleteCard = ({ athlete, language }) => {
  const [expanded, setExpanded] = useState(false);
  const copy = rosterCopy[language] || rosterCopy.EN;
  const id = `athlete-${athlete.id}`, match = athlete.latest_match, wellness = athlete.wellness;
  const name = `${athlete.first_name} ${athlete.last_name}`;
  const statuses = { FULLTRAIN: 'FIT', RTP: 'RTP', OUT: 'OUT' };
  const alert = wellness?.alert_level?.toLowerCase();
  const toggle = () => setExpanded(value => !value);
  return <article className={`roster-card ${expanded ? 'is-expanded' : ''}`} data-testid={`athlete-card-${athlete.id}`}>
    <button type="button" className="roster-card-main" onClick={toggle} aria-expanded={expanded}
      aria-controls={`${id}-details`} aria-label={`${name} · ${expanded ? copy.close : copy.details}`}
      data-testid={`${id}-expand`}>
      <div className="roster-card-header">
        <div className="roster-card-identity">
          <h2 data-testid={`${id}-name`}>{name}</h2>
          <div className="roster-card-meta">
            <span className="roster-position" data-testid={`${id}-position`}>{athlete.primary_position || '—'}</span>
            {athlete.jersey_number != null && <span data-testid={`${id}-jersey`}>#{athlete.jersey_number}</span>}
            <span data-testid={`${id}-teams`}>{athlete.teams.length ? athlete.teams.map(team => team.team_code).join(' · ') : copy.noTeam}</span>
          </div>
        </div>
        <span className={`roster-status status-${athlete.status?.toLowerCase()}`} data-testid={`${id}-status`}>
          {statuses[athlete.status] || athlete.status || '—'}
        </span>
      </div>
      <div className="roster-kpis">
        <RosterMetric id={`${id}-weight`} label={copy.weight} metric={athlete.weight} unit="kg" deltaUnit="kg" language={language} copy={copy}
          subtitle={rosterDate(athlete.weight.date, language)} comparison={copy.weightPeriod} />
        <RosterMetric id={`${id}-load`} label={copy.load} metric={athlete.load} unit="AU" deltaUnit="%" language={language} copy={copy} comparison={copy.loadPeriod} />
        <RosterMetric id={`${id}-training`} label={copy.training} metric={athlete.training_rpe} deltaUnit={copy.points} language={language} copy={copy} comparison={copy.trainingPeriod} />
        <RosterMetric id={`${id}-match`} label={copy.match} metric={athlete.match_rpe} deltaUnit={copy.points} language={language} copy={copy} comparison={copy.matchPeriod} />
      </div>
      <div className="roster-wellness" data-testid={`${id}-wellness`}>
        <span>{copy.wellness}</span>
        <span className="roster-wellness-value" data-testid={`${id}-wellness-value`}>
          {wellness ? <>{rosterNumber(wellness.score, language)}{wellness.score != null && ' / 100'}
            {alert && <span className={`roster-alert roster-alert-${alert}`} data-testid={`${id}-wellness-alert`}>{copy[alert] || wellness.alert_level}</span>}</> : copy.noWellness}
        </span>
        <ChevronDown className="roster-chevron" size={16} aria-hidden="true" />
      </div>
    </button>
    {expanded && <div id={`${id}-details`} className="roster-card-details" data-testid={`${id}-details`}>
      <div className="roster-detail-label" data-testid={`${id}-latest-match-label`}>{copy.latestMatch}</div>
      {match ? <div className="roster-exposure">
        <div className="min-w-0">
          <div className="roster-match-title" data-testid={`${id}-match-title`}>{match.opponent || match.title || (match.event_type === 'FRIENDLY_MATCH' ? copy.friendly : copy.competitive)}</div>
          <div className="roster-match-date" data-testid={`${id}-match-date`}>{rosterDate(match.date, language)} · {match.event_type === 'FRIENDLY_MATCH' ? copy.friendly : copy.competitive}</div>
        </div>
        <div className="roster-played" data-testid={`${id}-match-minutes`}>
          <span>{match.minutes == null ? '—' : `${match.minutes}′`}</span>
          <small>{match.minutes == null ? copy.missingMinutes : match.minutes === 0 ? 'DNP' : copy.minutes}</small>
        </div>
      </div> : <div className="roster-missing" data-testid={`${id}-match-missing`}>{copy.noMatch}</div>}
      <div className="roster-availability" data-testid={`${id}-availability`}>
        <span>{copy.availability}</span>
        <span>{statuses[athlete.status] || athlete.status || copy.missing}{athlete.rtp?.injury_type ? ` · ${athlete.rtp.injury_type}` : ''}
          {athlete.rtp?.current_phase ? ` · ${copy.phase} ${athlete.rtp.current_phase.replace('PHASE_', '')}` : ''}</span>
      </div>
      <Link to={`/staff/roster/${athlete.id}`} className="roster-profile-link" data-testid={`view-athlete-${athlete.id}`}>
        {copy.profile}<ArrowUpRight size={16} aria-hidden="true" />
      </Link>
    </div>}
  </article>;
};